// Построение waveform (peaks + durationMs) — SPEC §4.3.
//
// Waveform строится НА ЛЕТУ из живого аудио через AnalyserNode-метр (отдельный анализ-граф, НЕ
// источник записи: запись делает MediaRecorder). Метр раз в ~sampleIntervalMs снимает уровень
// (0..1), уровни копятся; на финале массив отсчётов даунсэмплится до фиксированного числа корзин
// (peakBuckets), нормируется к 0..1 и округляется до короткого фикса. durationMs — ИЗМЕРЕННАЯ
// длительность записи (по таймеру), НЕ контейнерный таймкод (у стримингового webm он рваный —
// рецепт Diktum). При недоступном/спящем AudioContext деградируем к плоскому плейсхолдеру —
// отправка из-за waveform НИКОГДА не блокируется (инвариант 3 не страдает).
/** Аккумулятор уровней активной записи. Создаётся на старте записи, читается на финале. */
export class WaveformBuilder {
    samples = [];
    /** Добавить снимок уровня (0..1). Значения за пределами диапазона клампятся. */
    push(level) {
        if (!Number.isFinite(level))
            return;
        this.samples.push(clamp01(level));
    }
    /** Сколько отсчётов накоплено (для тестов/диагностики). */
    get sampleCount() {
        return this.samples.length;
    }
    /**
     * Срез нарастающих peaks для индикации во время записи (§4.3): даунсэмпл до `liveBuckets`
     * накопленного «хвоста». Дёшев — зовётся на каждом тике метра.
     */
    live(liveBuckets) {
        if (this.samples.length === 0)
            return [];
        return downsample(this.samples, Math.min(liveBuckets, this.samples.length));
    }
    /**
     * Финальный Waveform для VoiceContent (§4.3). `durationMs` — измеренная длительность записи
     * (вход, не из контейнера). Если уровней нет (метр недоступен/спал) — плоский плейсхолдер.
     */
    finalize(durationMs, peakBuckets) {
        const buckets = Math.max(1, peakBuckets);
        if (this.samples.length === 0) {
            return { peaks: placeholder(buckets), durationMs };
        }
        const peaks = normalize(downsample(this.samples, buckets)).map(round2);
        return { peaks, durationMs };
    }
}
/** Плоский плейсхолдер фиксированной амплитуды (деградация §4.3). */
export function placeholder(buckets) {
    return new Array(Math.max(1, buckets)).fill(0.5);
}
/**
 * Даунсэмпл массива до `buckets` корзин: каждая корзина = max уровень в своём окне (огибающая
 * пиков, как у аудиоредакторов). Если отсчётов меньше корзин — отдаём, что есть (без растяжки).
 */
export function downsample(samples, buckets) {
    const n = samples.length;
    const b = Math.max(1, Math.min(buckets, n));
    if (n === 0)
        return [];
    if (n <= b)
        return samples.slice();
    const out = new Array(b);
    for (let i = 0; i < b; i++) {
        const start = Math.floor((i * n) / b);
        const end = Math.max(start + 1, Math.floor(((i + 1) * n) / b));
        let peak = 0;
        for (let j = start; j < end && j < n; j++) {
            const v = samples[j] ?? 0;
            if (v > peak)
                peak = v;
        }
        out[i] = peak;
    }
    return out;
}
/** Нормировка к 0..1 по максимуму (тихую запись «поднимаем», чтобы рисунок был виден). */
export function normalize(peaks) {
    let max = 0;
    for (const p of peaks)
        if (p > max)
            max = p;
    if (max <= 0)
        return peaks.map(() => 0);
    return peaks.map((p) => clamp01(p / max));
}
function clamp01(v) {
    if (v < 0)
        return 0;
    if (v > 1)
        return 1;
    return v;
}
function round2(v) {
    return Math.round(v * 100) / 100;
}
//# sourceMappingURL=waveform.js.map